import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Answer } from '@/components/question/forms/shared/Answer';
import { QuizAnswerCreateDTO } from '@/models/Answer';
import { usePedWorkspaceStatus } from '@/data/context/PedWorkspaceContext';

type QuizAnswerTextProps = {
  answer: QuizAnswerCreateDTO;
  isEditing: boolean;
  onStartEdit: () => void;
  onFinishEdit: () => void;
  onUpdateText: (text: string) => void;
  onSwitchToMedia: () => void;
  onToggleCorrect: () => void;
  onDelete: () => void;
  isLoading?: boolean;
};

export default function QuizAnswerText({
  answer,
  isEditing,
  onStartEdit,
  onFinishEdit,
  onUpdateText,
  onSwitchToMedia,
  onToggleCorrect,
  onDelete,
  isLoading = false,
}: QuizAnswerTextProps) {
  const { isLimitedEdition } = usePedWorkspaceStatus();
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.quizForm',
  });

  return (
    <Answer isCorrect={answer.isCorrect} isEditing={isEditing}>
      <ConditionalRenderer
        condition={isEditing}
        fallback={<Answer.TextPreview text={answer.text} />}
      >
        <Answer.TextInput
          initialValue={answer.text ?? ''}
          placeholder={t('optionPlaceholder')}
          onConfirm={text => {
            onUpdateText(text);
            onFinishEdit();
          }}
          onCancel={onFinishEdit}
        />
      </ConditionalRenderer>
      <Answer.MoreOptions>
        <Answer.MoreOptions.EditText
          onClick={onStartEdit}
          disabled={isLoading}
        />
        <Answer.MoreOptions.EditMedia
          onClick={onSwitchToMedia}
          disabled={isLimitedEdition}
        />
        <Answer.MoreOptions.ToggleCorrect
          isCorrect={answer.isCorrect}
          onClick={onToggleCorrect}
          disabled={isLimitedEdition || isLoading}
        />
        <Answer.MoreOptions.Delete
          onClick={onDelete}
          disabled={isLimitedEdition || isLoading}
        />
      </Answer.MoreOptions>
    </Answer>
  );
}
